import { Component, OnInit, OnDestroy, Inject } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { 
  SessionWarningData, 
  SessionWarningResult, 
  SessionWarningComponentState 
} from '../../interfaces/auth.interfaces';
import { AuthService } from '../../services/auth.service';
import { ToastService } from '../../services/toast.service';

/**
 * Componente modal para advertencias de expiración de sesión
 * Siguiendo principios SOLID:
 * - Single Responsibility: Solo maneja la UI de advertencia de sesión
 * - Open/Closed: Extensible para diferentes tipos de advertencias
 * - Dependency Inversion: Depende de abstracciones (servicios)
 */
@Component({
  selector: 'app-session-warning-modal',
  templateUrl: './session-warning-modal.component.html',
  styleUrl: './session-warning-modal.component.scss'
})
export class SessionWarningModalComponent implements OnInit, OnDestroy {
  
  // ==================== ESTADO DEL COMPONENTE ====================
  
  state: SessionWarningComponentState = {
    isVisible: false,
    countdown: 0,
    isExtending: false,
    hasExtended: false,
    canExtend: true
  };

  warningData: SessionWarningData | null = null;
  private destroy$ = new Subject<void>();
  private countdownInterval: any;

  // ==================== PROPIEDADES CALCULADAS ====================

  get formattedTimeRemaining(): string {
    return this.formatTime(this.state.countdown);
  }

  get progressPercentage(): number {
    if (!this.warningData) return 100;
    const total = 300; // 5 minutos por defecto
    return Math.max(0, (this.state.countdown / total) * 100);
  }

  get progressColorClass(): string {
    const percentage = this.progressPercentage;
    if (percentage > 50) return 'bg-green-500';
    if (percentage > 25) return 'bg-yellow-500';
    return 'bg-red-500';
  }

  get warningIconClass(): string {
    const percentage = this.progressPercentage;
    if (percentage > 50) return 'text-yellow-500';
    if (percentage > 25) return 'text-orange-500';
    return 'text-red-500';
  }

  // ==================== CONSTRUCTOR ====================

  constructor(
    private authService: AuthService,
    private toastService: ToastService
  ) {}

  // ==================== LIFECYCLE HOOKS ====================

  ngOnInit(): void {
    console.log('SessionWarningModal inicializado');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.clearCountdownInterval();
    console.log('SessionWarningModal destruido');
  }

  // ==================== MÉTODOS PÚBLICOS ====================

  /**
   * Muestra el modal de advertencia con los datos proporcionados
   */
  showWarning(data: SessionWarningData): void {
    console.log('Mostrando advertencia de sesión:', data);
    
    this.warningData = data;
    this.state = {
      isVisible: true,
      countdown: data.remainingSeconds,
      isExtending: false,
      hasExtended: false,
      canExtend: data.canExtendSession
    };

    // Iniciar countdown visual
    this.startCountdown();
  }

  /**
   * Oculta el modal de advertencia
   */
  hideWarning(): void {
    console.log('Ocultando advertencia de sesión');
    
    this.state.isVisible = false;
    this.warningData = null;
    this.clearCountdownInterval();
  }

  /**
   * Maneja el click en "Continuar Sesión"
   */
  async onExtendSession(): Promise<void> {
    if (this.state.isExtending || !this.state.canExtend) {
      return;
    }

    console.log('Usuario solicitó extender sesión');
    this.state.isExtending = true;

    try {
      // Extender sesión (incluye renovación de token y reset de timer de inactividad)
      await this.authService.extendSession().toPromise();
      
      this.state.hasExtended = true;
      this.state.isExtending = false;

      // Mostrar mensaje de éxito
      this.toastService.showToast('success', '¡Sesión extendida exitosamente!');

      // Ocultar modal después de un breve delay
      setTimeout(() => {
        this.hideWarning();
      }, 1500);

    } catch (error) {
      console.error('Error al extender sesión:', error);
      this.state.isExtending = false;
      
      // Si falla la extensión, proceder con logout
      this.toastService.showToast('error', 'No se pudo extender la sesión. Cerrando sesión...');
      
      setTimeout(() => {
        this.onLogout();
      }, 2000);
    }
  }

  /**
   * Maneja el click en "Cerrar Sesión"
   */
  onLogout(): void {
    console.log('Usuario eligió cerrar sesión');
    this.hideWarning();
    this.authService.logoutLocal();
  }

  /**
   * Maneja el click en el backdrop para cerrar (opcional)
   */
  onBackdropClick(event: Event): void {
    // Por defecto no permitimos cerrar haciendo click fuera
    // ya que es una advertencia crítica
    event.stopPropagation();
  }

  /**
   * Maneja teclas del teclado
   */
  onKeyDown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'Enter':
        if (this.state.canExtend && !this.state.isExtending) {
          this.onExtendSession();
        }
        break;
      case 'Escape':
        // No permitir cerrar con Escape por seguridad
        break;
    }
  }

  // ==================== MÉTODOS PRIVADOS ====================

  private startCountdown(): void {
    this.clearCountdownInterval();

    this.countdownInterval = setInterval(() => {
      if (this.state.countdown > 0) {
        this.state.countdown--;
      } else {
        // Tiempo agotado, cerrar sesión automáticamente
        this.clearCountdownInterval();
        this.onSessionTimeout();
      }
    }, 1000);
  }

  private clearCountdownInterval(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
  }

  private onSessionTimeout(): void {
    console.log('Tiempo de advertencia agotado - cerrando sesión');
    this.toastService.showToast('warning', 'Sesión cerrada por inactividad');
    this.hideWarning();
    this.authService.logoutLocal();
  }

  private formatTime(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    
    if (minutes > 0) {
      return `${minutes}:${secs.toString().padStart(2, '0')}`;
    } else {
      return `${secs}s`;
    }
  }

  // ==================== MÉTODOS UTILITARIOS ====================

  /**
   * Obtiene el estado actual del componente
   */
  getState(): SessionWarningComponentState {
    return { ...this.state };
  }

  /**
   * Verifica si el modal está visible
   */
  isVisible(): boolean {
    return this.state.isVisible;
  }

  /**
   * Obtiene el resultado de la advertencia (para integración con otros servicios)
   */
  getResult(): SessionWarningResult {
    if (this.state.hasExtended) {
      return { action: 'extend', extendedSuccessfully: true };
    } else if (this.state.countdown <= 0) {
      return { action: 'timeout' };
    } else {
      return { action: 'logout' };
    }
  }
}
